import React, { useEffect, useState } from 'react';
import { Select, Form, InputNumber, type FormInstance, Divider, Tooltip } from 'antd';
import { type DefaultOptionType } from 'antd/es/select';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Space, Text, Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { isArray } from 'lodash';
import { Big } from 'big.js';
import cx from 'classnames';

import { JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import useServiceScope from '../hooks/useServiceScope';
import { BookingStore } from 'store';
import { ServiceScopeModal } from '.';
import styles from '../location.module.scss';

export namespace ProductList {
  export type Props = {
    products: DefaultOptionType[] | undefined;
    onSelect: (products: any) => void;
    form: FormInstance;
    isRequired?: boolean;
  } & WithOptionalDataTest;

  export const $: React.FC<Props> = observer(({ products, form, onSelect, isRequired }) => {
    const { productSelection } = BookingStore;
    const emptySelection = { id: null, qty: 1 };
    const [selectedProducts, setSelectedProducts] = useState<JsonData>(
      isArray(productSelection?.products) ? productSelection?.products : [emptySelection],
    );
    const [totalPrice, setTotalPrice] = useState<string>('0');
    const [serviceScopeId, setServiceScopeId] = useState<string | null>(null);
    const { serviceScopeTitle, serviceScope } = useServiceScope(serviceScopeId, products);
    const hasServiceScope = (id: string) => {
      return !!products?.find((product) => product.details.ID.toString() === id)?.details.service_scope;
    };
    const isProductPrepaid = (id: number) => {
      return !!products?.find((product) => product.value === id)?.details.officeworks_prepaid;
    };
    useEffect(() => {
      // format product selection
      const productValue = selectedProducts[0]?.id
        ? selectedProducts.filter((selectedProduct) => selectedProduct.id)
        : undefined;

      // Update total price
      setTotalPrice(
        selectedProducts
          ?.reduce((total: any, selectedProduct: JsonDataItem) => {
            const product = products?.find((item) => item.value === selectedProduct.id);
            const price = product ? Big(product.details.total).toFixed(2) : 0;
            return Big(total).plus(Big(price)).times(Big(selectedProduct.qty));
          }, 0)
          .toFixed(2),
      );

      // Update the form field data and form state when component data changed
      onSelect(productValue);
    }, [selectedProducts]);

    useEffect(() => {
      // Filter out selected products that don't exist in the updated list
      const filteredSelectedProducts = selectedProducts.filter((selectedProduct) =>
        products?.some((product) => product.value === selectedProduct.id),
      );

      // show empty selection if no selection
      filteredSelectedProducts.length === 0 && setSelectedProducts([emptySelection]);

      // Update the form field with the selected products
      if (filteredSelectedProducts[0]?.id) {
        setSelectedProducts(filteredSelectedProducts);
      } else {
        // align form data value if no selection in the component
        form.setFieldsValue({
          products: undefined,
        });
      }
    }, [products]);

    const handleProductChange = (value: string, index: number) => {
      const updatedProducts = [...selectedProducts];
      updatedProducts[index].id = value;
      setSelectedProducts(updatedProducts);
    };

    const handleQuantityChange = (value: number, index: number) => {
      const updatedProducts = [...selectedProducts];
      updatedProducts[index].qty = value;
      setSelectedProducts(updatedProducts);
    };

    const handleAddProduct = () => {
      setSelectedProducts([...selectedProducts, emptySelection]);
    };

    const handleRemoveProduct = (index: number) => {
      const updatedProducts = [...selectedProducts];
      updatedProducts?.splice(index, 1);
      setSelectedProducts(updatedProducts.length ? updatedProducts : [emptySelection]);
    };

    return (
      <div className={styles.location__products}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
          <Form.Item name="products" required rules={[{ required: true }]}>
            <div>
              {selectedProducts.map((selectedProduct, index) => (
                <div className={styles.location__productsItem} key={index}>
                  <Select
                    value={selectedProduct.id}
                    onChange={(value) => {
                      return handleProductChange(value, index);
                    }}
                    style={{ width: '100%', marginRight: 8 }}
                    placeholder="Select a product"
                    optionFilterProp="children"
                    className={cx(
                      styles.location__productsSelect,
                      isRequired && styles.location__productsSelect_required,
                    )}
                    showSearch
                  >
                    {products
                      ?.filter(
                        (product) =>
                          !selectedProducts
                            .filter((item) => item.id !== selectedProduct?.id) // exclude the current selection
                            .map((selected) => selected.id) // get remaining selected product ID's
                            .includes(product.value), // remove from available products already selected
                      )
                      .map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                  </Select>
                  {hasServiceScope(selectedProduct?.id) && (
                    <Tooltip title="Service Scope" placement="top">
                      <InfoCircleOutlined
                        onClick={() => setServiceScopeId(selectedProduct.id)}
                        className={styles.location__productsSelectScope}
                      />
                    </Tooltip>
                  )}
                  {selectedProduct?.id && (
                    <>
                      <InputNumber
                        value={selectedProduct.qty}
                        onChange={(value) => {
                          handleQuantityChange(value || 1, index);
                        }}
                        min={1}
                        width={55}
                        className={cx(
                          styles.location__productsQty,
                          isRequired && styles.location__productsQty_required,
                        )}
                        formatter={(value) => (value ? String(value).replace(/[^0-9.]/g, '') : '')}
                      />

                      <div className={styles.location__productsPrice}>
                        {selectedProduct.id && (
                          <Text.$>
                            {isProductPrepaid(selectedProduct.id) && (
                              <Text.$ type="danger" size={Text.Size.SM}>
                                (prepaid)
                              </Text.$>
                            )}
                            {' $'}
                            {Big(
                              products?.find((product) => product.details.ID.toString() === selectedProduct.id)?.details
                                ?.total || '0',
                            )
                              .times(selectedProduct.qty || '0')
                              .toFixed(2)}{' '}
                            inc GST
                          </Text.$>
                        )}
                        <Button.$
                          dataTestId="remove-product-1"
                          size={Button.Size.Small}
                          type={Button.Type.Link}
                          className={styles.location__productsRemove}
                          onClick={() => {
                            handleRemoveProduct(index);
                          }}
                        >
                          Remove
                        </Button.$>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {selectedProducts.slice(-1)[0].id && (
                <Button.$
                  type={Button.Type.Dashed}
                  block
                  dataTestId="add-another-product"
                  className={styles.location__productsAction}
                  onClick={handleAddProduct}
                  style={{ marginTop: 8 }}
                >
                  + Add another service
                </Button.$>
              )}
            </div>
          </Form.Item>
        </Space.$>

        {selectedProducts.filter((prod) => prod.id).length > 0 && (
          <>
            <Divider className={styles.location__productsDivider} />
            <div className={styles.location__productsTotal}>
              <Text.$ size={Text.Size.SM}>
                Total <br />
                <b>{selectedProducts.filter((prod) => prod.id).length}</b> items
              </Text.$>
              <Text.$ strong>${totalPrice} inc GST</Text.$>
            </div>
          </>
        )}
        <ServiceScopeModal.$
          isOpen={!!serviceScopeId}
          onCancel={() => setServiceScopeId(null)}
          content={serviceScope}
          title={serviceScopeTitle}
        />
      </div>
    );
  });
}
